<template>
  <div class="dialog">
    <span class="cls" @click="oth_showGuide(false)"></span>
    <div class="paper">
      
      <!-- Header -->
      <div class="header">
        <h2><i class="ph-fill ph-book-open"></i> 가이드북</h2>
        <span @click="oth_showGuide(false)">
          <i class="ph ph-x"></i>
        </span>
      </div>

      <div class="body">
        <!-- 좌측: 가이드 목록 -->
        <div class="guide-list">
          <div class="search-box">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="가이드 검색..."
            />
          </div>

          <div class="categories">
            <div 
              v-for="category in filteredCategories" 
              :key="category.id"
              class="category"
            >
              <div class="category-header" @click="toggleCategory(category.id)">
                <div class="category-title">
                  <i :class="category.icon"></i>
                  <span>{{ category.title }}</span>
                </div>
                <i class="ph" :class="expandedCategories.includes(category.id) ? 'ph-caret-up' : 'ph-caret-down'"></i>
              </div>

              <transition name="expand">
                <ul v-if="expandedCategories.includes(category.id)" class="guide-items">
                  <li 
                    v-for="guide in category.guides" 
                    :key="guide.id"
                    :class="{ active: selectedGuide?.id === guide.id }"
                    @click="selectGuide(guide)"
                  >
                    <i :class="guide.icon"></i>
                    <span>{{ guide.title }}</span>
                  </li>
                </ul>
              </transition>
            </div>
          </div>
        </div>

        <!-- 우측: 가이드 상세 -->
        <div class="guide-detail">
          <template v-if="selectedGuide">
            <div class="detail-header">
              <i :class="selectedGuide.icon"></i>
              <div>
                <h3>{{ selectedGuide.title }}</h3>
                <p>{{ selectedGuide.summary }}</p>
              </div>
            </div>

            <!-- GIF/이미지 영역 -->
            <div class="detail-media">
              <img 
                v-if="selectedGuide.gif" 
                :src="selectedGuide.gif" 
                :alt="selectedGuide.title"
              />
              <div v-else class="media-placeholder">
                <i :class="selectedGuide.icon"></i>
                <span>미리보기 준비중</span>
              </div>
            </div>

            <!-- 설명 -->
            <div class="detail-content">
              <div 
                v-for="(step, idx) in selectedGuide.steps" 
                :key="idx" 
                class="step"
              >
                <div class="step-number">{{ idx + 1 }}</div>
                <div class="step-content">
                  <strong>{{ step.title }}</strong>
                  <p>{{ step.desc }}</p>
                </div>
              </div>
            </div>

            <!-- 팁 -->
            <div v-if="selectedGuide.tip" class="detail-tip">
              <i class="ph-fill ph-lightbulb"></i>
              <p>{{ selectedGuide.tip }}</p>
            </div>
          </template>

          <!-- 미선택 상태 -->
          <template v-else>
            <div class="empty-state">
              <i class="ph ph-book-open-text"></i>
              <p>좌측에서 가이드를 선택해주세요</p>
            </div>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'GuideDialog',
  props: {
    oth_showGuide: Function
  },
  setup() {
    const searchQuery = ref('')
    const selectedGuide = ref(null)
    const expandedCategories = ref(['client', 'activity'])

    // 가이드 데이터
    const categories = ref([
      {
        id: 'client',
        title: '고객 관리',
        icon: 'ph-fill ph-users',
        guides: [
          {
            id: 'customer-register',
            title: '고객 등록하기',
            icon: 'ph-fill ph-user-plus',
            summary: '새로운 고객을 등록하는 방법',
            gif: '/guide/customer-register.gif',
            steps: [
              { title: '고객관리 열기', desc: '좌측 메뉴에서 고객 > 고객관리를 클릭합니다.' },
              { title: '새 고객 버튼', desc: '우측 상단의 "새 고객" 버튼을 클릭합니다.' },
              { title: '정보 입력', desc: '이름, 연락처 등 기본 정보를 입력합니다.' },
              { title: '저장하기', desc: '모든 정보 입력 후 "저장" 버튼을 클릭합니다.' }
            ],
            tip: '주민등록번호를 입력하면 생년월일과 성별이 자동으로 입력됩니다.'
          },
          {
            id: 'customer-search',
            title: '고객 검색하기',
            icon: 'ph-fill ph-magnifying-glass',
            summary: '등록된 고객을 빠르게 찾는 방법',
            gif: '/guide/customer-search.gif',
            steps: [
              { title: '고객관리 열기', desc: '좌측 메뉴에서 고객 > 고객관리를 클릭합니다.' },
              { title: '검색창 활용', desc: '상단 검색창에 이름, 연락처, 태그 등을 입력합니다.' },
              { title: '필터 사용', desc: '태그, VIP 등급 등으로 필터링할 수 있습니다.' }
            ],
            tip: '자주 연락하는 고객은 VIP로 설정해두면 빠르게 찾을 수 있어요.'
          },
          {
            id: 'customer-consult',
            title: '상담하기',
            icon: 'ph-fill ph-chats',
            summary: '고객 상담을 진행하는 방법',
            gif: '/guide/customer-consult.gif',
            steps: [
              { title: '상담하기 열기', desc: '좌측 메뉴에서 고객 > 상담하기를 클릭합니다.' },
              { title: '고객 선택', desc: '상담할 고객을 검색하여 선택합니다.' },
              { title: '보장분석', desc: '고객의 현재 보장 현황을 분석합니다.' },
              { title: '상담 내용 기록', desc: '상담 내용과 다음 액션을 기록합니다.' }
            ],
            tip: '상담 후 다음 연락 일정을 바로 등록하면 잊지 않아요.'
          }
        ]
      },
      {
        id: 'activity',
        title: '활동 관리',
        icon: 'ph-fill ph-briefcase',
        guides: [
          {
            id: 'calendar',
            title: '일정 관리',
            icon: 'ph-fill ph-calendar',
            summary: '일정을 등록하고 관리하는 방법',
            gif: '/guide/calendar.gif',
            steps: [
              { title: '일정관리 열기', desc: '좌측 메뉴에서 활동 > 일정관리를 클릭합니다.' },
              { title: '날짜 선택', desc: '캘린더에서 일정을 추가할 날짜를 클릭합니다.' },
              { title: '일정 입력', desc: '일정 제목, 시간, 내용을 입력합니다.' },
              { title: '알림 설정', desc: '필요시 알림을 설정할 수 있습니다.' }
            ],
            tip: '반복 일정은 한 번만 등록해도 자동으로 반복됩니다.'
          },
          {
            id: 'goal',
            title: '목표 설정',
            icon: 'ph-fill ph-target',
            summary: '월별 목표를 설정하는 방법',
            gif: '/guide/goal.gif',
            steps: [
              { title: '목표설정 열기', desc: '좌측 메뉴에서 활동 > 목표설정을 클릭합니다.' },
              { title: '목표 항목 선택', desc: '설정할 목표 항목을 선택합니다.' },
              { title: '목표치 입력', desc: '이번 달 달성할 목표 수치를 입력합니다.' }
            ],
            tip: '작은 목표부터 시작해서 점점 늘려가는 것이 좋아요.'
          }
        ]
      },
      {
        id: 'tools',
        title: '도구',
        icon: 'ph-fill ph-wrench',
        guides: [
          {
            id: 'crm',
            title: 'CRM 자동화',
            icon: 'ph-fill ph-robot',
            summary: '고객에게 자동으로 메시지 보내기',
            gif: '/guide/crm.gif',
            steps: [
              { title: 'CRM 열기', desc: '좌측 메뉴에서 도구 > CRM을 클릭합니다.' },
              { title: '템플릿 선택', desc: '발송할 메시지 템플릿을 선택합니다.' },
              { title: '대상 설정', desc: '메시지를 받을 고객을 선택합니다.' },
              { title: '발송하기', desc: '내용 확인 후 발송 버튼을 클릭합니다.' }
            ],
            tip: '생일, 보험 만기일 등에 자동 발송을 설정해보세요.'
          },
          {
            id: 'sketch',
            title: '스케치 도구',
            icon: 'ph-fill ph-pencil',
            summary: '그림으로 쉽게 설명하기',
            gif: '/guide/sketch.gif',
            steps: [
              { title: '스케치 열기', desc: '좌측 메뉴에서 도구 > 스케치를 클릭합니다.' },
              { title: '템플릿 선택', desc: '원하는 배경 템플릿을 선택합니다.' },
              { title: '그리기', desc: '펜, 형광펜 등으로 자유롭게 그립니다.' },
              { title: '저장/공유', desc: '완성된 스케치를 저장하거나 공유합니다.' }
            ],
            tip: '고객 상담 시 보장분석을 그림으로 설명하면 이해가 쉬워요.'
          }
        ]
      },
      {
        id: 'community',
        title: '커뮤니티',
        icon: 'ph-fill ph-users-three',
        guides: [
          {
            id: 'qna',
            title: '지식인',
            icon: 'ph-fill ph-graduation-cap',
            summary: '궁금한 것을 질문하고 답변받기',
            gif: '/guide/qna.gif',
            steps: [
              { title: '지식인 열기', desc: '좌측 메뉴에서 버즈 > 지식인을 클릭합니다.' },
              { title: '질문 검색', desc: '먼저 비슷한 질문이 있는지 검색해보세요.' },
              { title: '새 질문 작성', desc: '없다면 "질문하기" 버튼으로 새 질문을 작성합니다.' }
            ],
            tip: '자세한 질문일수록 좋은 답변을 받을 수 있어요.'
          },
          {
            id: 'claim-ex',
            title: '청구사례',
            icon: 'ph-fill ph-hand-heart',
            summary: '실제 청구 사례 공유하기',
            gif: '/guide/claim-ex.gif',
            steps: [
              { title: '청구사례 열기', desc: '좌측 메뉴에서 버즈 > 청구사례를 클릭합니다.' },
              { title: '사례 둘러보기', desc: '다른 설계사들의 청구 사례를 확인합니다.' },
              { title: '내 사례 공유', desc: '"사례 작성" 버튼으로 내 경험을 공유합니다.' }
            ],
            tip: '개인정보는 반드시 블러 처리 후 올려주세요.'
          }
        ]
      }
    ])

    // 카테고리 토글
    const toggleCategory = (categoryId) => {
      const idx = expandedCategories.value.indexOf(categoryId)
      if (idx > -1) {
        expandedCategories.value.splice(idx, 1)
      } else {
        expandedCategories.value.push(categoryId)
      }
    }

    // 가이드 선택
    const selectGuide = (guide) => {
      selectedGuide.value = guide
    }

    // 검색 필터
    const filteredCategories = computed(() => {
      if (!searchQuery.value.trim()) {
        return categories.value
      }

      const query = searchQuery.value.toLowerCase()
      return categories.value
        .map(cat => ({
          ...cat,
          guides: cat.guides.filter(g => 
            g.title.toLowerCase().includes(query) ||
            g.summary.toLowerCase().includes(query)
          )
        }))
        .filter(cat => cat.guides.length > 0)
    })

    return {
      searchQuery,
      selectedGuide,
      expandedCategories,
      categories,
      filteredCategories,
      toggleCategory,
      selectGuide
    }
  }
}
</script>

<style scoped>
.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-top);
  backdrop-filter: blur(2px);
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  width: 90%;
  max-width: 1000px;
  height: 85%;
  max-height: 700px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.paper > .header {
  width: 100%;
  height: var(--font-size-3xl);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
  box-sizing: border-box;
}

.paper > .header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.paper > .header > h2 > i {
  color: var(--color-brand);
}

.paper > .header > span {
  width: var(--font-size-3xl);
  height: var(--font-size-3xl);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
}

.paper > .header > span:hover {
  background: var(--color-background-light);
}

.paper > .header > span > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

/* Body */
.body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* 좌측: 가이드 목록 */
.guide-list {
  width: 280px;
  border-right: 1px solid var(--color-background-light);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin: 12px;
  background: var(--color-background-light);
  border-radius: 10px;
}

.search-box > i {
  font-size: 18px;
  color: var(--color-text-third);
}

.search-box > input {
  flex: 1;
  background: none;
  border: none;
  font-size: 14px;
  color: var(--color-text-primary);
  outline: none;
}

.search-box > input::placeholder {
  color: var(--color-text-third);
}

.categories {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
}

.category {
  margin-bottom: 8px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.2s;
}

.category-header:hover {
  background: var(--color-background-light);
}

.category-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-title > i {
  font-size: 20px;
  color: var(--color-brand);
}

.category-title > span {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.category-header > i {
  font-size: 16px;
  color: var(--color-text-third);
}

.guide-items {
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: 20px;
}

.guide-items > li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}

.guide-items > li:hover {
  background: var(--color-background-light);
}

.guide-items > li.active {
  background: var(--color-brand);
}

.guide-items > li > i {
  font-size: 18px;
  color: var(--color-text-secondary);
}

.guide-items > li.active > i {
  color: #fff;
}

.guide-items > li > span {
  font-size: 14px;
  color: var(--color-text-secondary);
}

.guide-items > li.active > span {
  color: #fff;
  font-weight: 500;
}

/* 우측: 가이드 상세 */
.guide-detail {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.detail-header > i {
  font-size: 40px;
  color: var(--color-brand);
  flex-shrink: 0;
}

.detail-header > div > h3 {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 6px 0;
}

.detail-header > div > p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
}

/* 미디어 영역 */
.detail-media {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--color-background-light);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.detail-media > img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.media-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.media-placeholder > i {
  font-size: 48px;
  color: var(--color-text-third);
}

.media-placeholder > span {
  font-size: 14px;
  color: var(--color-text-third);
}

/* 단계별 설명 */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.step {
  display: flex;
  gap: 16px;
}

.step-number {
  width: 28px;
  height: 28px;
  background: var(--color-brand);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content > strong {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.step-content > p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

/* 팁 */
.detail-tip {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
}

.detail-tip > i {
  font-size: 24px;
  color: #fbbf24;
  flex-shrink: 0;
}

.detail-tip > p {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

/* 빈 상태 */
.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.empty-state > i {
  font-size: 64px;
  color: var(--color-text-third);
}

.empty-state > p {
  font-size: 16px;
  color: var(--color-text-third);
  margin: 0;
}

/* 펼치기 애니메이션 */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* 모바일 대응 */
@media (max-width: 768px) {
  .body {
    flex-direction: column;
  }

  .guide-list {
    width: 100%;
    max-height: 40%;
    border-right: none;
    border-bottom: 1px solid var(--color-background-light);
  }

  .guide-detail {
    padding: 16px;
  }

  .detail-header > i {
    font-size: 32px;
  }

  .detail-header > div > h3 {
    font-size: 18px;
  }
}
</style>
